import React, { useCallback, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiLink,
  FiRefreshCw,
  FiRotateCcw,
  FiSearch,
  FiShield,
  FiUser,
  FiXCircle,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { functions } from '../firebase';
import layoutStyles from '../styles/layout.module.css';
import styles from './PaymentsAdmin.module.css';

function formatMoneyFromMinor(minor, currency = 'CAD') {
  const amount = Number(minor || 0) / 100;
  try {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: String(currency || 'CAD').toUpperCase() }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency || 'CAD'}`;
  }
}

function formatTimestamp(timestamp = null) {
  const millis = Number(timestamp?.millis || 0);
  return millis ? new Date(millis).toLocaleString() : '—';
}

function statusIcon(status = '') {
  const normalized = String(status || '').trim();
  if (['fully_enabled', 'transferred', 'paid'].includes(normalized)) return <FiCheckCircle className={styles.iconSuccess} />;
  if (['funds_held', 'held', 'setup_required', 'charge_enabled', 'partially_transferred'].includes(normalized)) return <FiClock className={styles.iconWarn} />;
  if (['refunded', 'disputed', 'not_connected'].includes(normalized)) return <FiAlertCircle className={styles.iconMuted} />;
  return <FiXCircle className={styles.iconMuted} />;
}

function toneClass(status = '') {
  const normalized = String(status || '').trim();
  if (['fully_enabled', 'transferred', 'paid'].includes(normalized)) return styles.toneSuccess;
  if (['funds_held', 'held', 'setup_required', 'charge_enabled', 'partially_transferred'].includes(normalized)) return styles.toneWarn;
  return styles.toneMuted;
}

export default function PaymentsAdmin() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [nextCursor, setNextCursor] = useState('');
  const [selectedUserUid, setSelectedUserUid] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [manualAccountId, setManualAccountId] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [actionBusy, setActionBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const selectedUser = useMemo(
    () => users.find((entry) => entry.uid === selectedUserUid) || null,
    [selectedUserUid, users]
  );
  const selectedOrder = useMemo(
    () => orders.find((entry) => entry.orderId === selectedOrderId) || null,
    [orders, selectedOrderId]
  );

  const loadOverview = useCallback(async ({ append = false, cursor = '' } = {}) => {
    try {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError('');
      const callable = httpsCallable(functions, 'listStripeAdminOverview');
      const response = await callable({
        query,
        pageSize: 25,
        pageCursor: cursor,
      });
      const data = response?.data || {};
      const nextUsers = Array.isArray(data.users) ? data.users : [];
      const nextOrders = Array.isArray(data.orders) ? data.orders : [];
      setUsers(nextUsers);
      setOrders((current) => append ? current.concat(nextOrders.filter((item) => !current.some((entry) => entry.orderId === item.orderId))) : nextOrders);
      setNextCursor(String(data.nextCursor || '').trim());
      if (!append) {
        setSelectedUserUid((current) => current && nextUsers.some((entry) => entry.uid === current) ? current : (nextUsers[0]?.uid || ''));
        setSelectedOrderId((current) => current && nextOrders.some((entry) => entry.orderId === current) ? current : (nextOrders[0]?.orderId || ''));
      }
    } catch (err) {
      setError(err?.message || 'Unable to load Stripe admin data.');
      if (!append) {
        setUsers([]);
        setOrders([]);
        setNextCursor('');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [query]);

  React.useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const runAction = useCallback(async ({ name, callableName, payload = {}, successMessage = '' }) => {
    try {
      setActionBusy(name);
      setError('');
      setNotice('');
      const callable = httpsCallable(functions, callableName);
      await callable({ ...payload, note: actionNote });
      setNotice(successMessage || 'Action completed.');
      await loadOverview();
    } catch (err) {
      setError(err?.message || 'Unable to complete admin action.');
    } finally {
      setActionBusy('');
    }
  }, [actionNote, loadOverview]);

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar variant="back" backLabel="Back" onBack={() => navigate('/admin')} rightActionLabel="Home" onRightAction={() => navigate('/home')} />
      <div className={`${layoutStyles.detailMain} ${styles.pageShell}`.trim()}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Admin</span>
          <h1>Payments</h1>
          <p>Inspect Stripe Connect readiness, track payout state, retry held transfers, reassign connected accounts, and refund orders from one workspace.</p>
        </header>

        <section className={styles.toolbar}>
          <label className={styles.searchField}>
            <FiSearch />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name, email, uid, account id, payment intent, charge id, or transfer group"
            />
          </label>
          <button type="button" className={styles.primaryAction} onClick={() => loadOverview()} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>
        </section>

        {(notice || error) ? (
          <section className={styles.noticeCard}>
            {notice ? <p className={styles.noticeText}>{notice}</p> : null}
            {error ? <p className={styles.errorText}>{error}</p> : null}
          </section>
        ) : null}

        <section className={styles.grid}>
          <section className={styles.panel}>
            <div className={styles.panelHeader}>
              <div>
                <span className={styles.sectionEyebrow}>Users</span>
                <h2>Connect Accounts</h2>
              </div>
              <span className={styles.countBadge}>{users.length}</span>
            </div>
            <div className={styles.stack}>
              {users.map((user) => (
                <button
                  key={user.uid}
                  type="button"
                  className={`${styles.rowCard} ${selectedUserUid === user.uid ? styles.rowCardActive : ''}`.trim()}
                  onClick={() => {
                    setSelectedUserUid(user.uid);
                    setManualAccountId(user.connect?.accountId || user.stripeAccounts?.[0]?.id || '');
                  }}
                >
                  <div className={styles.rowTop}>
                    <strong>{user.name || user.email || user.uid}</strong>
                    <span className={`${styles.statusPill} ${toneClass(user.connect?.statusPhase)}`.trim()}>
                      {statusIcon(user.connect?.statusPhase)}
                      {String(user.connect?.statusPhase || 'not_connected').replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p>{user.email || 'No email'}</p>
                  <p className={styles.meta}>Account: {user.connect?.accountId || 'Not connected'}</p>
                  {user.duplicateAccountsDetected ? <p className={styles.warning}>Multiple Stripe accounts detected for this user.</p> : null}
                </button>
              ))}
              {!users.length && !loading ? <p className={styles.stateText}>No users matched this search.</p> : null}
            </div>
          </section>

          <section className={styles.panelWide}>
            <div className={styles.panelHeader}>
              <div>
                <span className={styles.sectionEyebrow}>Orders</span>
                <h2>Payments &amp; Payouts</h2>
              </div>
              <span className={styles.countBadge}>{orders.length}</span>
            </div>
            <div className={styles.stack}>
              {orders.map((order) => (
                <button
                  key={order.orderId}
                  type="button"
                  className={`${styles.rowCard} ${selectedOrderId === order.orderId ? styles.rowCardActive : ''}`.trim()}
                  onClick={() => setSelectedOrderId(order.orderId)}
                >
                  <div className={styles.rowTop}>
                    <strong>{order.productTitle || order.orderId}</strong>
                    <span className={`${styles.statusPill} ${toneClass(order.settlement?.status)}`.trim()}>
                      {statusIcon(order.settlement?.status)}
                      {String(order.settlement?.status || 'unknown').replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p>{order.artist?.name || 'Unknown artist'} · {formatMoneyFromMinor(order.amounts?.totalMinor, order.amounts?.currency)}</p>
                  <p className={styles.meta}>Payout: {String(order.payouts?.artist?.status || 'unknown').replace(/_/g, ' ')} · {order.artist?.accountId || 'No account id'}</p>
                  <p className={styles.meta}>{formatTimestamp(order.createdAt)}</p>
                </button>
              ))}
              {!orders.length && !loading ? <p className={styles.stateText}>No orders matched this search.</p> : null}
            </div>
            {nextCursor ? (
              <div className={styles.loadMoreRow}>
                <button type="button" className={styles.secondaryAction} onClick={() => loadOverview({ append: true, cursor: nextCursor })} disabled={loadingMore}>
                  {loadingMore ? 'Loading…' : 'Load More'}
                </button>
              </div>
            ) : null}
          </section>
        </section>

        <section className={styles.detailGrid}>
          <section className={styles.detailPanel}>
            <div className={styles.panelHeader}>
              <div>
                <span className={styles.sectionEyebrow}>User Detail</span>
                <h2>{selectedUser?.name || 'Select a user'}</h2>
              </div>
            </div>
            {selectedUser ? (
              <div className={styles.detailBody}>
                <div className={styles.metaGrid}>
                  <div className={styles.metaItem}><span><FiUser /> UID</span><strong>{selectedUser.uid}</strong></div>
                  <div className={styles.metaItem}><span><FiCreditCard /> Email</span><strong>{selectedUser.email || '—'}</strong></div>
                  <div className={styles.metaItem}><span><FiLink /> Canonical Account</span><strong>{selectedUser.connect?.accountId || '—'}</strong></div>
                  <div className={styles.metaItem}><span><FiShield /> Connect Status</span><strong>{String(selectedUser.connect?.statusPhase || 'not_connected').replace(/_/g, ' ')}</strong></div>
                </div>

                <div className={styles.actionRow}>
                  <button type="button" className={styles.primaryAction} onClick={() => runAction({ name: 'refresh-user', callableName: 'adminRefreshStripeConnectAccount', payload: { targetUid: selectedUser.uid }, successMessage: 'Stripe account refreshed.' })} disabled={Boolean(actionBusy)}>
                    <FiRefreshCw /> Refresh Stripe
                  </button>
                  <button type="button" className={styles.secondaryAction} onClick={() => runAction({ name: 'retry-user', callableName: 'adminRetryHeldStripePayouts', payload: { targetUid: selectedUser.uid }, successMessage: 'Held payouts retried for user.' })} disabled={Boolean(actionBusy)}>
                    <FiRotateCcw /> Retry Held Payouts
                  </button>
                  <button type="button" className={styles.secondaryAction} onClick={() => runAction({ name: 'disconnect-user', callableName: 'disconnectStripeConnectAccount', payload: { targetUid: selectedUser.uid }, successMessage: 'Connect account disconnected.' })} disabled={Boolean(actionBusy)}>
                    <FiXCircle /> Disconnect
                  </button>
                </div>

                <label className={styles.formField}>
                  <span>Manual canonical account id</span>
                  <input value={manualAccountId} onChange={(event) => setManualAccountId(event.target.value)} placeholder="acct_..." />
                </label>
                <button type="button" className={styles.primaryAction} onClick={() => runAction({ name: 'assign-user', callableName: 'adminAssignStripeConnectAccount', payload: { targetUid: selectedUser.uid, accountId: manualAccountId.trim() }, successMessage: 'Canonical Stripe account reassigned.' })} disabled={Boolean(actionBusy) || !manualAccountId.trim()}>
                  <FiLink /> Assign Canonical Account
                </button>

                <div className={styles.block}>
                  <h3>Discovered Stripe Accounts</h3>
                  <div className={styles.stack}>
                    {(selectedUser.stripeAccounts || []).map((account) => (
                      <div key={account.id} className={styles.inlineCard}>
                        <div className={styles.rowTop}>
                          <strong>{account.id}</strong>
                          <span className={`${styles.statusPill} ${toneClass(account.payoutsEnabled ? 'fully_enabled' : account.chargesEnabled ? 'charge_enabled' : 'setup_required')}`.trim()}>
                            {statusIcon(account.payoutsEnabled ? 'fully_enabled' : account.chargesEnabled ? 'charge_enabled' : 'setup_required')}
                            {account.payoutsEnabled ? 'fully enabled' : account.chargesEnabled ? 'can charge' : 'setup required'}
                          </span>
                        </div>
                        <p>{account.email || 'No email'} · {account.country || '—'}</p>
                      </div>
                    ))}
                    {!(selectedUser.stripeAccounts || []).length ? <p className={styles.stateText}>No Stripe accounts discovered for this uid.</p> : null}
                  </div>
                </div>
              </div>
            ) : <p className={styles.stateText}>Select a user to inspect Stripe account state.</p>}
          </section>

          <section className={styles.detailPanel}>
            <div className={styles.panelHeader}>
              <div>
                <span className={styles.sectionEyebrow}>Order Detail</span>
                <h2>{selectedOrder?.orderId || 'Select an order'}</h2>
              </div>
            </div>
            {selectedOrder ? (
              <div className={styles.detailBody}>
                <div className={styles.metaGrid}>
                  <div className={styles.metaItem}><span><FiUser /> Buyer</span><strong>{selectedOrder.buyer?.name || selectedOrder.buyer?.email || '—'}</strong></div>
                  <div className={styles.metaItem}><span><FiShield /> Artist</span><strong>{selectedOrder.artist?.name || '—'}</strong></div>
                  <div className={styles.metaItem}><span><FiCreditCard /> Amount</span><strong>{formatMoneyFromMinor(selectedOrder.amounts?.totalMinor, selectedOrder.amounts?.currency)}</strong></div>
                  <div className={styles.metaItem}><span><FiClock /> Created</span><strong>{formatTimestamp(selectedOrder.createdAt)}</strong></div>
                </div>

                <div className={styles.badgeRow}>
                  <span className={`${styles.statusPill} ${toneClass(selectedOrder.settlement?.status)}`.trim()}>{statusIcon(selectedOrder.settlement?.status)}{String(selectedOrder.settlement?.status || 'unknown').replace(/_/g, ' ')}</span>
                  <span className={`${styles.statusPill} ${toneClass(selectedOrder.payouts?.artist?.status)}`.trim()}>{statusIcon(selectedOrder.payouts?.artist?.status)}{String(selectedOrder.payouts?.artist?.status || 'unknown').replace(/_/g, ' ')}</span>
                </div>

                <div className={styles.actionRow}>
                  <button type="button" className={styles.primaryAction} onClick={() => runAction({ name: 'retry-order', callableName: 'adminRetryHeldStripePayouts', payload: { orderId: selectedOrder.orderId }, successMessage: 'Held payout retried for order.' })} disabled={Boolean(actionBusy)}>
                    <FiRotateCcw /> Retry This Payout
                  </button>
                  <button type="button" className={styles.secondaryAction} onClick={() => runAction({ name: 'refund-order', callableName: 'adminRefundStripeOrder', payload: { orderId: selectedOrder.orderId }, successMessage: 'Refund requested from Stripe.' })} disabled={Boolean(actionBusy)}>
                    <FiRefreshCw /> Refund Order
                  </button>
                </div>

                <label className={styles.formField}>
                  <span>Admin note</span>
                  <textarea rows={3} value={actionNote} onChange={(event) => setActionNote(event.target.value)} placeholder="Reason for reassignment, refund, or retry." />
                </label>

                <div className={styles.block}>
                  <h3>Payout Snapshot</h3>
                  <div className={styles.metaGrid}>
                    <div className={styles.metaItem}><span>Connected account</span><strong>{selectedOrder.payouts?.artist?.connectedAccountId || '—'}</strong></div>
                    <div className={styles.metaItem}><span>Transfer id</span><strong>{selectedOrder.payouts?.artist?.transferId || '—'}</strong></div>
                    <div className={styles.metaItem}><span>Hold reason</span><strong>{selectedOrder.payouts?.artist?.holdReason || '—'}</strong></div>
                    <div className={styles.metaItem}><span>Last error</span><strong>{selectedOrder.payouts?.artist?.lastErrorCode || '—'}</strong></div>
                  </div>
                  <p className={styles.stateText}>{selectedOrder.payouts?.artist?.lastErrorMessage || 'No payout error recorded.'}</p>
                </div>

                <div className={styles.block}>
                  <h3>Stripe Identifiers</h3>
                  <div className={styles.metaGrid}>
                    <div className={styles.metaItem}><span>Payment intent</span><strong>{selectedOrder.orderId}</strong></div>
                    <div className={styles.metaItem}><span>Charge id</span><strong>{selectedOrder.payment?.chargeId || '—'}</strong></div>
                    <div className={styles.metaItem}><span>Transfer group</span><strong>{selectedOrder.payment?.transferGroup || '—'}</strong></div>
                    <div className={styles.metaItem}><span>Product</span><strong>{selectedOrder.productTitle || selectedOrder.productId || '—'}</strong></div>
                  </div>
                </div>
              </div>
            ) : <p className={styles.stateText}>Select an order to inspect its payout state.</p>}
          </section>
        </section>
      </div>
    </div>
  );
}
